<template>
  <div class="meetings-archive-view">
    <!-- HEADER -->
    <div class="page-header">
      <div class="header-left">
        <router-link to="/add-new-meeting">
          <button class="btn btn-primary">
            + Nowe spotkanie
          </button>
        </router-link>
      </div>

      <input
        v-model="search"
        class="search-input"
        type="search"
        placeholder="Wyszukaj spotkanie..."
      />
    </div>

    <!-- SUMMARY -->
    <div class="summary">
      <div class="summary-item">
        <span class="summary-label">Liczba spotkań</span>
        <span class="summary-value">{{ filteredMeetings.length }}</span>
      </div>

      <div class="summary-item">
        <span class="summary-label">Na dzisiaj</span>
        <span class="summary-value">{{ todayMeetingsCount }}</span>
      </div>

      <div class="summary-item">
        <span class="summary-label">Najbliższe</span>
        <span class="summary-value">{{ upcomingMeetingsCount }}</span>
      </div>
    </div>

    <!-- Formularz edycji (gdy aktywny) -->
    <div v-if="isEditing" class="edit-form" id="editForm">
      <div class="edit-form-header">
        <h3>✏️ Edycja spotkania</h3>
      </div>
      <div class="edit-form-body">
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Imię i nazwisko</label>
            <input 
              v-model="editedMeeting.client" 
              placeholder="Imię i nazwisko" 
              class="form-input"/>
          </div>
          <div class="form-group">
            <label class="form-label">Numer telefonu</label>
            <input 
              v-model="editedMeeting.phoneNr" 
              placeholder="Numer telefonu" 
              class="form-input"/>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">E-mail</label>
            <input 
              v-model="editedMeeting.email" 
              placeholder="E-mail" 
              type="email"
              class="form-input"/>
          </div>
          <div class="form-group">
            <label class="form-label">Adres spotkania</label>
            <input 
              v-model="editedMeeting.adres" 
              placeholder="Adres spotkania" 
              class="form-input"/>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Data spotkania</label>
            <input 
              v-model="editedMeeting.date" 
              type="date" 
              class="form-input"/>
          </div>
          <div class="form-group">
            <label class="form-label">Godzina spotkania</label>
            <input 
              v-model="editedMeeting.time" 
              type="time" 
              class="form-input"/>
          </div>
        </div>
        <div class="edit-form-actions">
          <button class="btn btn-success" @click="saveEdit()">
            ✓ Zapisz
          </button>
          <button class="btn btn-secondary" @click="cancelEdit()">
            ✕ Anuluj
          </button>
        </div>
      </div>
    </div>

    <!-- TABLE / CARDS -->
    <div class="table-container desktop-view">
      <table class="meetings-table">
        <thead>
          <tr>
            <th>Data i godzina</th>
            <th>Klient</th>
            <th>Adres</th>
            <th>Telefon</th>
            <th>E-mail</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="meeting in filteredMeetings" 
            :key="meeting.id" 
            class="meeting-row"
            :class="{'meeting-today': isMeetingToday(meeting.date)}">
            <td>
              <span class="date-badge" :class="{'today-badge': isMeetingToday(meeting.date)}">
                {{ meeting.date }}, {{ meeting.time }}
              </span>
            </td>
            <td>{{ meeting.client }}</td>
            <td>
              <a 
                :href="'https://maps.google.com/?q=' + encodeURIComponent(meeting.adres)" 
                target="_blank" 
                class="address-link">
                📍 {{ meeting.adres }}
              </a>
            </td>
            <td>
              <a v-if="meeting.phoneNr" :href="'tel:' + meeting.phoneNr" class="phone-link">
                📞 {{ meeting.phoneNr }}
              </a>
              <span v-else>-</span>
            </td>
            <td>{{ meeting.email }}</td>
            <td>
              <div class="action-buttons">
                <button class="btn-edit" @click="startEditing(meeting)">
                  Edytuj
                </button>
                <button class="btn-delete" @click="deleteNewMeeting(meeting.id)">
                  Usuń
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- MOBILE CARDS VIEW -->
    <div class="mobile-cards-view">
      <div 
        v-for="meeting in filteredMeetings" 
        :key="meeting.id" 
        class="meeting-card"
        :class="{'meeting-today-card': isMeetingToday(meeting.date)}">
        <div class="card-header">
          <h3 class="card-title">{{ meeting.client }}</h3>
          <span class="card-date" :class="{'today-badge': isMeetingToday(meeting.date)}">
            {{ meeting.date }}, {{ meeting.time }}
          </span>
        </div>
        <div class="card-body">
          <div class="card-info">
            <span class="info-icon">📍</span>
            <a 
              :href="'https://maps.google.com/?q=' + encodeURIComponent(meeting.adres)" 
              target="_blank" 
              class="address-link">
              {{ meeting.adres }}
            </a>
          </div>
          <div class="card-info" v-if="meeting.phoneNr">
            <span class="info-icon">📞</span>
            <a :href="'tel:' + meeting.phoneNr" class="phone-link">
              {{ meeting.phoneNr }}
            </a>
          </div>
          <div class="card-info" v-if="meeting.email">
            <span class="info-icon">✉️</span>
            <span>{{ meeting.email }}</span>
          </div>
        </div>
        <div class="card-actions">
          <button class="btn-edit" @click="startEditing(meeting)">
            ✏️ Edytuj
          </button>
          <button class="btn-delete" @click="deleteNewMeeting(meeting.id)">
            🗑️ Usuń
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";

export default {
  data() {
    return {
      lastMeetings: 100,
      search: "",
      isEditing: null,
      editedMeeting: {
        client: "",
        adres: "",
        phoneNr: "",
        email: "",
        date: "",
        time: "",
      }
    };
  },
  mounted(){
    // Pobierz dane tylko jeśli nie ma żadnych spotkań w store
    if (!this.newMeetings || !Array.isArray(this.newMeetings) || this.newMeetings.length === 0) {
      this.getData();
    }
  },
  computed: {
    ...mapState(["userProfile","newMeetings"]),
    // sortowanie spotkań od najbliższych do najpóźniejszych 
    sortedNewMeetings() {
      return [...this.newMeetings].sort((a, b) => {
        const aDate = new Date(`${a.date}T${a.time}`);
        const bDate = new Date(`${b.date}T${b.time}`);
        return aDate - bDate; 
      });
    },
    filteredMeetings() {
      if (!this.search) {
        return this.sortedNewMeetings;
      }
      return this.sortedNewMeetings.filter((meeting) => {
        const searchLower = this.search.toLowerCase();
        return (
          (meeting.client && meeting.client.toLowerCase().includes(searchLower)) ||
          (meeting.adres && meeting.adres.toLowerCase().includes(searchLower)) ||
          (meeting.phoneNr && meeting.phoneNr.includes(searchLower)) ||
          (meeting.email && meeting.email.toLowerCase().includes(searchLower))
        );
      });
    },
    todayMeetingsCount() {
      const today = new Date().toISOString().split('T')[0];
      return this.sortedNewMeetings.filter(m => m.date === today).length;
    },
    upcomingMeetingsCount() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return this.sortedNewMeetings.filter(m => {
        const meetingDate = new Date(m.date);
        return meetingDate >= today;
      }).length;
    }
  },
  methods: {
    getData() {
      this.$store.dispatch("getNewMeetings",{
        lastMeetings: this.lastMeetings});
    },
    // funkcja sprawdzająca czy spotkanie jest dzisiaj
    isMeetingToday(meetingDateStr){
      const today = new Date();
      const meetingDate = new Date(meetingDateStr);

      return (
        today.getFullYear() === meetingDate.getFullYear() &&
        today.getMonth() === meetingDate.getMonth() &&
        today.getDate() === meetingDate.getDate()
      );
    },
    // funkcja usuwająca
    deleteNewMeeting(id){
      if (confirm('Czy na pewno chcesz usunąć to spotkanie?')) {
        this.$store.dispatch("deleteNewMeeting", id);
      }
    },
    // funkcje edytujące spotkanie
    startEditing(newMeeting){
      this.isEditing = newMeeting.id;
      this.editedMeeting = {...newMeeting};
      setTimeout(() => {
        const editFormEl = document.getElementById('editForm');
        if (editFormEl) {
          editFormEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    },
    saveEdit(){
      const data = { ...this.editedMeeting, id: this.isEditing };
      this.$store.dispatch("updateNewMeeting", data);
      this.isEditing = null;
    },
    cancelEdit() {
      this.isEditing = null;
      this.editedMeeting = {
        client: "",
        adres: "",
        phoneNr: "",
        email: "",
        date: "",
        time: "",
      };
    }
  }
}
</script>
<style scoped>
.meetings-archive-view {
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
}

/* HEADER */
.page-header {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  padding: 24px;
  margin-bottom: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.tabs {
  display: flex;
  gap: 4px;
  background: white;
  padding: 4px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.tab {
  padding: 10px 20px;
  border-radius: 6px;
  color: #6c757d;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  border: none;
  background: transparent;
  cursor: pointer;
}

.tab:hover {
  background: #f8f9fa;
  color: #495057;
}

.tab-filter:hover {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.search-input {
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  min-width: 300px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* FILTRY */
.filters-section {
  background: white;
  padding: 20px 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
  display: flex;
  gap: 20px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-input {
  padding: 10px 14px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  color: #2c3e50;
  transition: all 0.2s ease;
  min-width: 150px;
}

.filter-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* SUMMARY */
.summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.summary-item {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  overflow: hidden;
}

.summary-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.summary-item:nth-child(2)::before {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
}

.summary-item:nth-child(3)::before {
  background: linear-gradient(135deg, #fd7e14 0%, #ffc107 100%);
}

.summary-label {
  font-size: 13px;
  color: #6c757d;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: 12px;
}

.summary-value {
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-left: 12px;
}

/* EDIT FORM */
.edit-form {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
  overflow: hidden;
  border: 2px solid #667eea;
}

.edit-form-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px 24px;
}

.edit-form-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.edit-form-body {
  padding: 24px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-input {
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 15px;
  color: #2d3748;
  transition: all 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.edit-form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px solid #f0f0f0;
}

.btn-success {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
}

.btn-success:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.4);
}

.btn-secondary {
  background: #e2e8f0;
  color: #4a5568;
}

.btn-secondary:hover {
  background: #cbd5e0;
}

/* TABLE */
.table-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  overflow: hidden;
}

.meetings-table {
  width: 100%;
  border-collapse: collapse;
}

.meetings-table thead {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.meetings-table th {
  padding: 16px 20px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.meetings-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.2s ease;
}

.meetings-table tbody tr:hover {
  background: #f8f9fa;
}

.meeting-today {
  background: rgba(255, 193, 7, 0.1) !important;
}

.meeting-today:hover {
  background: rgba(255, 193, 7, 0.15) !important;
}

.meetings-table td {
  padding: 16px 20px;
  color: #2c3e50;
  font-size: 14px;
}

.date-badge {
  display: inline-block;
  padding: 6px 12px;
  background: #e9ecef;
  border-radius: 6px;
  font-weight: 600;
  font-size: 13px;
  color: #495057;
}

.today-badge {
  background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%);
  color: white;
}

.address-link {
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
}

.address-link:hover {
  color: #764ba2;
  text-decoration: underline;
}

.phone-link {
  color: #48bb78;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s ease;
}

.phone-link:hover {
  color: #38a169;
  text-decoration: underline;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn-edit {
  padding: 8px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-edit:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-delete {
  padding: 8px 16px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-delete:hover {
  background: #c82333;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
}

/* MOBILE CARDS VIEW */
.mobile-cards-view {
  display: none;
}

.meeting-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.meeting-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.meeting-today-card {
  border: 2px solid #ffc107;
  box-shadow: 0 2px 12px rgba(255, 193, 7, 0.3);
}

.card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 16px 20px;
  color: white;
}

.card-title {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
}

.card-date {
  display: inline-block;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
}

.card-body {
  padding: 20px;
}

.card-info {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  font-size: 14px;
  color: #2c3e50;
}

.card-info:last-child {
  margin-bottom: 0;
}

.info-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.card-actions {
  padding: 16px 20px;
  background: #f8f9fa;
  display: flex;
  gap: 10px;
  border-top: 1px solid #e9ecef;
}

.card-actions .btn-edit,
.card-actions .btn-delete {
  flex: 1;
  padding: 12px;
  font-size: 14px;
  justify-content: center;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* RESPONSIVE */
@media (max-width: 1024px) {
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .meetings-table {
    font-size: 13px;
  }
  
  .meetings-table th,
  .meetings-table td {
    padding: 12px 16px;
  }
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .header-left {
    flex-direction: column;
  }
  
  .search-input {
    min-width: 100%;
  }
  
  .summary {
    grid-template-columns: 1fr;
  }
  
  /* Ukryj tabelę, pokaż karty na mobile */
  .desktop-view {
    display: none;
  }
  
  .mobile-cards-view {
    display: block;
  }
  
  .meetings-archive-view {
    padding: 12px;
  }
  
  .edit-form-body {
    padding: 16px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>